import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../../shared/models/user';
import { AppConstants } from '../../app.constants';

const STORAGE_TOKEN_KEY = AppConstants.KEYS_LOCAL_STORAGE.ISO_ACCESS_TOKEN;
const STORAGE_USER_KEY = AppConstants.KEYS_LOCAL_STORAGE.ISO_USUARIO;

function hasPassword(user: User | null): boolean {
  return user?.password !== undefined;
}

/**
 * The user without the password, which must never be kept in the browser
 * session (it is readable by any script on the page). Users without one are
 * returned as they are.
 */
function withoutPassword(user: User): User {
  if (!hasPassword(user)) {
    return user;
  }
  const safeUser = { ...user };
  delete safeUser.password;
  return safeUser;
}

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  private readonly _token$ = new BehaviorSubject<string | null>(
    this.loadFromStorage(STORAGE_TOKEN_KEY),
  );
  private readonly _user$ = new BehaviorSubject<User | null>(
    this.loadUserFromStorage(),
  );

  readonly token$ = this._token$.asObservable();
  readonly user$ = this._user$.asObservable();
  readonly isAuthenticated$ = this._token$.pipe(map((t) => !!t));

  setSession(token: string, user: User): void {
    const safeUser = withoutPassword(user);
    // Set the user first: consumers reacting to isAuthenticated$/token$ (e.g. the
    // consent reconcile) read the user synchronously to build the auth header.
    this._user$.next(safeUser);
    this._token$.next(token);
    localStorage.setItem(STORAGE_TOKEN_KEY, token);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(safeUser));
  }

  clearSession(): void {
    this._token$.next(null);
    this._user$.next(null);
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
  }

  /**
   * Removes the per-user data kept in the browser (keys starting with
   * `AppConstants.USER_SCOPED_STORAGE_PREFIX`), so the next person using a
   * shared device cannot read it.
   *
   * @remarks
   * Only call this on explicit logout and account deletion. `clearSession()`
   * also runs after profile updates, on alternate login page visits and when a
   * token refresh fails; wiping there would discard work the user expects to
   * keep, such as a field calibration in progress.
   */
  clearUserScopedData(): void {
    try {
      const keys = Array.from({ length: localStorage.length }, (_, index) =>
        localStorage.key(index),
      );
      keys
        .filter((key): key is string =>
          Boolean(key?.startsWith(AppConstants.USER_SCOPED_STORAGE_PREFIX)),
        )
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage may be blocked; there is nothing left to clear in that case
    }
  }

  getToken(): string | null {
    return this._token$.getValue();
  }

  getUser(): User | null {
    return this._user$.getValue();
  }

  private loadFromStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @remarks
   * The former password change flow saved the new password in plain text in
   * the stored session. It is removed from storage the first time such a
   * session is loaded.
   */
  private loadUserFromStorage(): User | null {
    try {
      const raw = localStorage.getItem(STORAGE_USER_KEY);
      if (!raw) {
        return null;
      }
      const user = JSON.parse(raw) as User;
      if (hasPassword(user)) {
        const safeUser = withoutPassword(user);
        localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(safeUser));
        return safeUser;
      }
      return user;
    } catch {
      return null;
    }
  }
}
